import { z } from 'zod';
import type { UnionToIntersection } from 'type-fest';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);

/*
 * AI HANDLER RESULT V3
 */
export const coordinates = z.tuple([
  z.number().describe('X coordinate'),
  z.number().describe('Y coordinate'),
]);

export type Coordinates = z.infer<typeof coordinates>;

export const baseContour = z.object({
  contour: z.array(coordinates).describe('List of coordinates'),
  hierarchy: z
    .array(z.number())
    .min(4)
    .max(4)
    .describe('[next_idx, previous_idx, parent_idx, first_child_idx]. -1 means a non-existent index'),
});

export type BaseContourType = z.infer<typeof baseContour>;

const BlurMeasurement = z.object({
  contours: z.array(
    baseContour
      .extend({
        area_um2: z.number().describe('Area of the contour in square micrometers'),
      })
      .describe('Blur contour (level 0 coordinates)'),
  ),
  total_area_um2: z.number().describe('Area in square micrometer, holes are taken into account'),
  patches: z
    .array(
      z
        .object({
          coord: coordinates.describe('Coordinates of the center of the patch at level 0'),
          magnification: z.number().describe('Magnification level this patch was taken at'),
          path: z.string().describe('$slide_scan_id/output-ai/patch_$x.png'),
          score: z.number().min(0).max(100).describe('0 is normal, 100 is blurry'),
        })
        .describe('Generated thumbnail of 512x512 pixels'),
    )
    .min(5)
    .max(5)
    .describe('Contains blurry and non-burry patches'),
});

const MissingTissueMeasurement = z.object({
  contours: z.array(
    baseContour
      .extend({
        area_mm2: z.number().describe('Area in square millimeters'),
      })
      .describe('Missing tissue contour (macro level coordinates)'),
  ),
  parameters: z.object({
    bottom_offset_px: z.number().describe('Offset in pixels from the bottom of the slide'),
    left_offset_px: z.number().describe('Offset in pixels from the left of the slide (label area)'),
    right_offset_px: z.number().describe('Offset in pixels from the right of the slide'),
    top_offset_px: z.number().describe('Offset in pixels from the top of the slide'),
  }),
  total_area_mm2: z.number().describe('Total estimated area in square millimeters'),
});

const TissueMeasurement = z.object({
  contours: z.array(
    baseContour
      .extend({
        area_um2: z.number().describe('Area of the contour in square micrometers'),
      })
      .describe('Tissue contour (level 0 coordinates)'),
  ),
  total_area_um2: z.number().describe('Area of the detection in square micrometer, holes are taken into account'),
});

const CutOffTissueMeasurement = z.object({
  contours_macro: z.array(
    baseContour
      .extend({
        length_mm: z.number().describe('Length of the contour in millimeters'),
      })
      .describe('Cut-off tissue contour (macro level coordinates)'),
  ),
  contours_wsi: z.array(
    baseContour
      .extend({
        length_mm: z.number().describe('Length of the contour in millimeters'),
      })
      .describe('Cut-off tissue contour (WSI level 0 coordinates)'),
  ),
  total_length_mm: z.number().describe('Total estimated length in millimeters'),
});

const MisalignedCoverSlipMeasurement = z.object({
  contours: z.array(
    baseContour
      .extend({
        length_mm: z.number().describe('Length of the contour in millimeters'),
      })
      .describe('Misaligned cover slip contour (level 0 coordinates)'),
  ),
  total_length_mm: z.number().describe('Total estimated length in millimeters'),
});

const MarkerOnTopOfTissueMeasurement = z.object({
  contours: z.array(
    baseContour
      .extend({
        area_mm2: z.number().describe('Area of the contour in square millimeters'),
      })
      .describe('Marker on top of tissue contour (level 0 coordinates)'),
  ),
  total_area_mm2: z.number().describe('Area of the detection in square millimeters, holes are taken into account'),
});

export const AIHandlerResult_v3 = z
  .object({
    schema_version: z.literal('3.0.0').describe('Version of the result.json schema'),
    task: z.object({
      log: z
        .string()
        .optional()
        .describe(
          'Path of the log file of this task : $slide_scan_id/output-ai/logs.txt . Can be missing if the task failed or the writing failed',
        ),
      result: z
        .enum(['SUCCESS', 'FAIL'])
        .describe('Status of the AI result, indicates the success or failure of the task.'),
      result_status: z.literal('SUCCESS').or(z.string()).describe('`SUCCESS` or a description of the failure'),
      slide_scan_id: z.string().describe('Identifier of the slide scan provided by the caller'),
    }),
    measurements: z
      .object({
        blur: BlurMeasurement,
        missing_tissue: MissingTissueMeasurement,
        tissue: TissueMeasurement,
      })
      .partial()
      .optional()
      .describe('Measurements detected by the AI handler. Only exists if task.result worth `SUCCESS`'),
    version: z.object({
      models: z.array(
        z.object({
          name: z.string().describe('Name of the model'),
          version: z.string().describe('Version of the model'),
        }),
      ),
      version: z.string().describe('Version of the AI handler'),
    }),
  })
  .describe('Complete schema of the AI handler result').openapi('AIHandlerResult_v3');

export const AIHandlerResult_v3_1_0 = AIHandlerResult_v3.extend({
  schema_version: z.literal('3.1.0').describe('Version of the result.json schema'),
  measurements: z
    .object({
      blur: BlurMeasurement,
      missing_tissue: MissingTissueMeasurement,
      tissue: TissueMeasurement,
      cut_off_tissue: CutOffTissueMeasurement,
      misaligned_cover_slip: MisalignedCoverSlipMeasurement,
    })
    .partial()
    .optional()
    .describe('Measurements detected by the AI handler. Only exists if task.result worth `SUCCESS`'),
}).describe('Complete schema of the AI handler result').passthrough().openapi('AIHandlerResult_v3_1_0');

export const AIHandlerResult_v3_2_0 = AIHandlerResult_v3_1_0.extend({
  schema_version: z.literal('3.2.0').describe('Version of the result.json schema'),
  measurements: z
    .object({
      blur: BlurMeasurement,
      missing_tissue: MissingTissueMeasurement,
      tissue: TissueMeasurement,
      cut_off_tissue: CutOffTissueMeasurement,
      misaligned_cover_slip: MisalignedCoverSlipMeasurement,
      marker_on_top_of_tissue: MarkerOnTopOfTissueMeasurement,
    })
    .partial()
    .optional()
    .describe('Measurements detected by the AI handler. Only exists if task.result worth `SUCCESS`'),
}).describe('Complete schema of the AI handler result').passthrough().openapi('AIHandlerResult_v3_2_0');

const MissingTissueMeasurement_v3_3_0 = MissingTissueMeasurement.extend({
  processed_region_contours: z
    .array(baseContour.describe('Processed region contour (macro level coordinates)'))
    .describe('Contours representing the processed region used for missing tissue detection'),
});

export const AIHandlerResult_v3_3_0 = AIHandlerResult_v3_2_0.extend({
  schema_version: z.literal('3.3.0').describe('Version of the result.json schema'),
  measurements: z
    .object({
      blur: BlurMeasurement,
      missing_tissue: MissingTissueMeasurement_v3_3_0,
      tissue: TissueMeasurement,
      cut_off_tissue: CutOffTissueMeasurement,
      misaligned_cover_slip: MisalignedCoverSlipMeasurement,
      marker_on_top_of_tissue: MarkerOnTopOfTissueMeasurement,
    })
    .partial()
    .optional()
    .describe('Measurements detected by the AI handler. Only exists if task.result worth `SUCCESS`'),
}).describe('Complete schema of the AI handler result').passthrough().openapi('AIHandlerResult_v3_3_0');

export const AIHandlerResult = z.discriminatedUnion('schema_version', [
  AIHandlerResult_v3,
  AIHandlerResult_v3_1_0,
  AIHandlerResult_v3_2_0,
  AIHandlerResult_v3_3_0,
]).openapi('AIHandlerResult');

export type AIHandlerResultType = z.infer<typeof AIHandlerResult>;
export type AIHandlerResultInputType = z.input<typeof AIHandlerResult>;

export type MeasurementsType = UnionToIntersection<NonNullable<AIHandlerResultInputType['measurements']>>;
export type MeasurementsKeys = keyof MeasurementsType;
export type AIHandlerSchemaVersion = AIHandlerResultType['schema_version'];

// TODO share colors with FE (needs refactor)
const ANNOTATION_COLORS = {
  out_of_focus_region: {
    stroke: 'rgba(4, 0, 255, 1)',
    fill: 'rgba(4, 0, 255, 0.2)',
  },
  cut_off_tissue: {
    stroke: 'rgba(167, 0, 133, 1)',
    fill: 'rgba(167, 0, 133, 0.2)',
  },
  misaligned_cover_slip: {
    stroke: 'rgba(0, 183, 255, 1)',
    fill: 'rgba(0, 183, 255, 0.2)',
  },
  marker_on_top_of_tissue: {
    stroke: 'rgba(252, 255, 66, 1)',
    fill: 'rgba(252, 255, 66, 0.2)',
  },
} as const;

export const MeasurementsTypeToColorMap: Record<
  Exclude<MeasurementsKeys, 'missing_tissue' | 'tissue'>,
  { stroke: string; fill: string }
> = {
  blur: ANNOTATION_COLORS.out_of_focus_region,
  cut_off_tissue: ANNOTATION_COLORS.cut_off_tissue,
  misaligned_cover_slip: ANNOTATION_COLORS.misaligned_cover_slip,
  marker_on_top_of_tissue: ANNOTATION_COLORS.marker_on_top_of_tissue,
};
